import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { servicesApi, type Service } from '@/lib/api';

export const useServices = () => {
  return useQuery({
    queryKey: ['services'],
    queryFn: servicesApi.getAll,
    staleTime: 5 * 60 * 1000, // 5 minutos
  });
};

export const useService = (id: string) => {
  return useQuery({
    queryKey: ['services', id],
    queryFn: () => servicesApi.getById(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutos
  });
};

export const useCreateService = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ service, deliverables, process }: { 
      service: Omit<Service, 'id'>; 
      deliverables: string[]; 
      process: string[]; 
    }) => servicesApi.create(service, deliverables, process),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] });
    },
  });
};

export const useUpdateService = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ 
      id, 
      updates, 
      deliverables, 
      process 
    }: { 
      id: string; 
      updates: Partial<Service>; 
      deliverables?: string[]; 
      process?: string[]; 
    }) => servicesApi.update(id, updates, deliverables, process),
    onSuccess: (updatedService) => {
      queryClient.invalidateQueries({ queryKey: ['services'] });
      queryClient.invalidateQueries({ queryKey: ['services', updatedService.id] });
    },
  });
};

export const useDeleteService = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => servicesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] });
    },
  });
};
